import React, { useEffect, useRef, useState } from 'react';

type V3 = [number, number, number];
type Mesh = {verts: V3[], faces: number[][]};

const TAU = Math.PI * 2;

function buildGearMesh(teeth = 16, toothDepth = 0.17, innerRatio = 0.5, halfThickness = 0.17): Mesh {
  const profile: {x: number, y: number}[] = [];
  const step = TAU / teeth;
  const root = 1 - toothDepth;
  for (let i = 0; i < teeth; i++) {
    const center = i * step;
    const angles = [
      center - step * 0.30,
      center - step * 0.17,
      center + step * 0.17,
      center + step * 0.30,
    ];
    const radii = [root, 1, 1, root];
    for (let k = 0; k < 4; k++) {
      profile.push({x: Math.cos(angles[k]) * radii[k], y: Math.sin(angles[k]) * radii[k]});
    }
  }

  const verts: V3[] = [];
  const outerFront: number[] = [];
  const outerBack: number[] = [];
  const innerFront: number[] = [];
  const innerBack: number[] = [];
  for (const {x, y} of profile) {
    const theta = Math.atan2(y, x);
    const ix = Math.cos(theta) * innerRatio;
    const iy = Math.sin(theta) * innerRatio;
    outerFront.push(verts.push([x, y, halfThickness]) - 1);
    outerBack.push(verts.push([x, y, -halfThickness]) - 1);
    innerFront.push(verts.push([ix, iy, halfThickness]) - 1);
    innerBack.push(verts.push([ix, iy, -halfThickness]) - 1);
  }

  const faces: number[][] = [];
  const count = profile.length;
  for (let i = 0; i < count; i++) {
    const j = (i + 1) % count;
    faces.push([outerFront[i], outerFront[j], innerFront[j], innerFront[i]]);
    faces.push([outerBack[j], outerBack[i], innerBack[i], innerBack[j]]);
    faces.push([outerFront[j], outerFront[i], outerBack[i], outerBack[j]]);
    faces.push([innerFront[i], innerFront[j], innerBack[j], innerBack[i]]);
  }
  return {verts, faces};
}

function buildSpringMesh(turns = 6, pathSegments = 132, tubeSegments = 10): Mesh {
  const verts: V3[] = [];
  const faces: number[][] = [];
  const helixRadius = 0.46;
  const tubeRadius = 0.115;
  const height = 2;
  const rings: number[][] = [];

  for (let i = 0; i <= pathSegments; i++) {
    const t = i / pathSegments;
    const angle = t * TAU * turns;
    const cx = Math.cos(angle) * helixRadius;
    const cz = Math.sin(angle) * helixRadius;
    const cy = (t - 0.5) * height;

    const tl = Math.hypot(TAU * turns * helixRadius, height);
    const tx = -Math.sin(angle) * TAU * turns * helixRadius / tl;
    const ty = height / tl;
    const tz = Math.cos(angle) * TAU * turns * helixRadius / tl;

    const rx = Math.cos(angle);
    const rz = Math.sin(angle);
    let bx = ty * rz;
    let by = tz * rx - tx * rz;
    let bz = -ty * rx;
    const bl = Math.hypot(bx, by, bz) || 1;
    bx /= bl; by /= bl; bz /= bl;
    const nx = by * tz - bz * ty;
    const ny = bz * tx - bx * tz;
    const nz = bx * ty - by * tx;

    const ring: number[] = [];
    for (let j = 0; j < tubeSegments; j++) {
      const a = (j / tubeSegments) * TAU;
      const ca = Math.cos(a) * tubeRadius;
      const sa = Math.sin(a) * tubeRadius;
      ring.push(verts.push([
        cx + rx * ca + nx * sa,
        cy + ny * sa,
        cz + rz * ca + nz * sa,
      ]) - 1);
    }
    rings.push(ring);
  }

  for (let i = 0; i < pathSegments; i++) {
    for (let j = 0; j < tubeSegments; j++) {
      const k = (j + 1) % tubeSegments;
      faces.push([rings[i][j], rings[i][k], rings[i + 1][k], rings[i + 1][j]]);
    }
  }

  const startCenter = verts.push([helixRadius, -height / 2, 0]) - 1;
  const endCenter = verts.push([helixRadius, height / 2, 0]) - 1;
  for (let j = 0; j < tubeSegments; j++) {
    const k = (j + 1) % tubeSegments;
    faces.push([startCenter, rings[0][j], rings[0][k]]);
    faces.push([endCenter, rings[pathSegments][k], rings[pathSegments][j]]);
  }

  return {verts, faces};
}

function buildVaseMesh(segments = 26, layers = 22): Mesh {
  const verts: V3[] = [];
  const faces: number[][] = [];
  const twist = 1.7;
  const radiusAt = (t: number) => 0.95 + 0.28 * Math.sin(Math.PI * (0.15 + 0.7 * t)) - 0.32 * t;
  const rings: number[][] = [];

  for (let i = 0; i <= layers; i++) {
    const t = i / layers;
    const y = (t - 0.5) * 2;
    const rotation = t * twist;
    const radius = radiusAt(t);
    const ring: number[] = [];
    for (let j = 0; j < segments; j++) {
      const angle = (j / segments) * TAU + rotation;
      ring.push(verts.push([Math.cos(angle) * radius, y, Math.sin(angle) * radius]) - 1);
    }
    rings.push(ring);
  }

  for (let i = 0; i < layers; i++) {
    for (let j = 0; j < segments; j++) {
      const k = (j + 1) % segments;
      faces.push([rings[i][j], rings[i][k], rings[i + 1][k], rings[i + 1][j]]);
    }
  }

  const bottom = verts.push([0, -1, 0]) - 1;
  for (let j = 0; j < segments; j++) {
    const k = (j + 1) % segments;
    faces.push([bottom, rings[0][k], rings[0][j]]);
  }

  return {verts, faces};
}

function buildLayerStackMesh(layers = 14, segments = 24, exponent = 3.2): Mesh {
  const verts: V3[] = [];
  const faces: number[][] = [];
  const height = 2;
  const thickness = 0.075;
  const radiusAt = (t: number) => 1 - 0.42 * t;

  for (let i = 0; i < layers; i++) {
    const t = i / (layers - 1);
    const y = (t - 0.5) * height;
    const radius = radiusAt(t);
    const rotation = t * 1.1;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const bottomRing: number[] = [];
    const topRing: number[] = [];

    for (let j = 0; j < segments; j++) {
      const angle = (j / segments) * TAU;
      const ca = Math.cos(angle);
      const sa = Math.sin(angle);
      const sx = Math.sign(ca) * Math.pow(Math.abs(ca), 2 / exponent);
      const sz = Math.sign(sa) * Math.pow(Math.abs(sa), 2 / exponent);
      const x = (sx * cos - sz * sin) * radius;
      const z = (sx * sin + sz * cos) * radius;
      bottomRing.push(verts.push([x, y - thickness / 2, z]) - 1);
      topRing.push(verts.push([x, y + thickness / 2, z]) - 1);
    }

    for (let j = 0; j < segments; j++) {
      const k = (j + 1) % segments;
      faces.push([bottomRing[j], bottomRing[k], topRing[k], topRing[j]]);
    }
    const bottomCenter = verts.push([0, y - thickness / 2, 0]) - 1;
    const topCenter = verts.push([0, y + thickness / 2, 0]) - 1;
    for (let j = 0; j < segments; j++) {
      const k = (j + 1) % segments;
      faces.push([bottomCenter, bottomRing[k], bottomRing[j]]);
      faces.push([topCenter, topRing[j], topRing[k]]);
    }
  }

  return {verts, faces};
}

const SHAPES = [
  {name: 'gear', build: buildGearMesh, scale: 1, spin: 0.00022},
  {name: 'spring', build: buildSpringMesh, scale: 0.92, spin: 0.00028},
  {name: 'vase', build: buildVaseMesh, scale: 0.82, spin: 0.00024},
  {name: 'layers', build: buildLayerStackMesh, scale: 0.86, spin: 0.0003},
];

function pickShape() {
  const requested = new URLSearchParams(window.location.search).get('model');
  return SHAPES.find(shape => shape.name === requested)
    ?? SHAPES[Math.floor(Math.random() * SHAPES.length)];
}

function shadeColor(shade: number) {
  const dark = [58, 74, 176];
  const light = [198, 207, 255];
  const t = 0.22 + 0.78 * Math.max(0, shade);
  const c = dark.map((d, i) => Math.round(d + (light[i] - d) * t));
  return `rgba(${c[0]},${c[1]},${c[2]},.94)`;
}

export default function HomeBackdrop() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [shape] = useState(pickShape);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;

    const {verts, faces} = shape.build();
    const light: V3 = [-0.42, -0.55, 0.72];
    const lightLength = Math.hypot(...light);
    const lx = light[0] / lightLength;
    const ly = light[1] / lightLength;
    const lz = light[2] / lightLength;

    let width = 0;
    let height = 0;
    let frame = 0;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const render = (elapsed: number) => {
      const angle = reducedMotion ? 0.7 : elapsed * shape.spin;
      const tilt = -0.42 + (reducedMotion ? 0 : Math.sin(elapsed * 0.00031) * 0.07);
      const cosY = Math.cos(angle);
      const sinY = Math.sin(angle);
      const cosX = Math.cos(tilt);
      const sinX = Math.sin(tilt);
      const cx = width / 2;
      const cy = height / 2;
      const scale = Math.min(width, height) * (width < 700 ? 0.52 : 0.29) * shape.scale;
      const perspective = 3.4;

      const rotated: V3[] = verts.map(([x, y, z]) => {
        const rx = x * cosY + z * sinY;
        const rz = -x * sinY + z * cosY;
        const ry = y * cosX - rz * sinX;
        return [rx, ry, y * sinX + rz * cosX];
      });

      const projected = rotated.map(([x, y, z]) => {
        const factor = perspective / (perspective - z);
        return [cx + x * factor * scale, cy + y * factor * scale, z] as V3;
      });

      context.clearRect(0, 0, width, height);
      const order = faces
        .map((face, index) => ({
          index,
          depth: face.reduce((sum, vertex) => sum + rotated[vertex][2], 0) / face.length,
        }))
        .sort((a, b) => a.depth - b.depth);

      for (const {index} of order) {
        const face = faces[index];
        const [a, b, c] = face.map(vertex => rotated[vertex]);
        const ux = b[0] - a[0];
        const uy = b[1] - a[1];
        const uz = b[2] - a[2];
        const vx = c[0] - a[0];
        const vy = c[1] - a[1];
        const vz = c[2] - a[2];
        const nx = uy * vz - uz * vy;
        const ny = uz * vx - ux * vz;
        const nz = ux * vy - uy * vx;
        const nLength = Math.hypot(nx, ny, nz) || 1;
        const shade = Math.abs((nx * lx + ny * ly + nz * lz) / nLength);
        const color = shadeColor(shade);

        context.beginPath();
        context.moveTo(projected[face[0]][0], projected[face[0]][1]);
        for (let i = 1; i < face.length; i++) {
          context.lineTo(projected[face[i]][0], projected[face[i]][1]);
        }
        context.closePath();
        context.fillStyle = color;
        context.strokeStyle = color;
        context.lineWidth = 0.7;
        context.fill();
        context.stroke();
      }
    };

    const tick = (time: number) => {
      render(time);
      frame = window.requestAnimationFrame(tick);
    };

    resize();
    if (reducedMotion) {
      render(0);
    } else {
      frame = window.requestAnimationFrame(tick);
    }

    const observer = new ResizeObserver(() => {
      resize();
      if (reducedMotion) render(0);
    });
    observer.observe(canvas);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [shape]);

  return <canvas ref={canvasRef} className="home-canvas" aria-hidden="true" />;
}
